import { FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { AsyncSearch, Loader } from 'components';
import { TextField, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useCustomerAPI } from 'hooks/use-customer-api';
import useTimeZone from 'hooks/use-timezone';
import { UnitTransferRequestParams, useUnitApi } from 'hooks/use-unit-api';
import { getReceivedDate, getUnit, getUnitByUnitNumber } from 'services';
import { ActiveUnit, Customer } from 'types';

import './renumber.scss';

class RenumberParams implements UnitTransferRequestParams {
  id?: number;
  customer?: Customer;
  chargeGateIn?: boolean = true;
  chargeGateOut?: boolean = true;
  ediGateIn?: boolean = true;
  ediGateOut?: boolean = true;
  transferDate?: string;
  unitNumber?: string;

  constructor(unit?: ActiveUnit | null) {
    this.id = unit?.id ?? undefined;
    this.customer = unit?.customer ?? undefined;
    this.unitNumber = unit?.unitNumber ?? undefined;
  }
}

const RenumberUnit: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const unitId = useMemo(() => Number(params.unitId), [params.unitId]);

  const { searchCustomers } = useCustomerAPI();
  const { transferUnit } = useUnitApi();
  const { dateTimeLocalToUtc, newZonedDayJs, utcToZonedDateTimeLocal } = useTimeZone();

  const [loading, setLoading] = useState<boolean>();
  const [receivedDate, setReceivedDate] = useState<string | null>();
  const [transferParams, setTransferParams] = useState(new RenumberParams());
  const [unit, setUnit] = useState<ActiveUnit | null>();

  useEffect(() => {
    if (!isNaN(unitId)) {
      getUnit(unitId).then(response => {
        setUnit(response);
        setTransferParams(new RenumberParams(response));
      });
      getReceivedDate(unitId).then(setReceivedDate);
    }
  }, [unitId, setUnit]);

  const handleRenumber = useCallback(
    (unitNumber?: string) => setTransferParams(prevState => ({ ...prevState, unitNumber })),
    [setTransferParams]
  );

  const handleSelect = useCallback(
    (customer?: Customer) => setTransferParams(prevState => ({ ...prevState, customer })),
    [setTransferParams]
  );

  const handleReset = useCallback(() => navigate(`/units/${unitId}`, { replace: true }), [unitId, navigate]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setLoading(true);
      if (!unit) {
        return;
      }
      const response = await transferUnit(transferParams);
      if (response) {
        navigate(`/units/${response.id}`, { replace: true });
      }
      setLoading(false);
    },
    [unit, transferParams, navigate, setLoading, transferUnit]
  );

  const validateUnitNumber = useCallback(
    async (input: string): Promise<ValidationResult> => {
      if (input === unit?.unitNumber) {
        return { status: ValidationStatus.DEFAULT, error: 'Unit number unchanged' };
      }
      const response = await getUnitByUnitNumber(input);
      if (response?.unitNumber === unit?.unitNumber) {
        return { status: ValidationStatus.DEFAULT };
      } else if (!response) {
        return { status: ValidationStatus.SUCCESS };
      } else {
        return { status: ValidationStatus.WARNING, error: `Unit ${input} is already active` };
      }
    },
    [unit?.unitNumber]
  );

  return (
    <Loader loading={loading || unit === undefined}>
      <ErrorDialog />
      <form id="renumber" className="page" onReset={handleReset} onSubmit={handleSubmit}>
        <div className="page-header">
          <h1>UNIT #{unit?.unitNumber ?? 'N/A'}</h1>
          <div className="grid-2">
            <button className="btn-outline" type="reset" disabled={!unitId}>
              CANCEL
            </button>
            <button className="btn-primary" type="submit" disabled={!unitId}>
              SUBMIT
            </button>
          </div>
        </div>
        <div className="page-content">
          <div className="card-primary">
            <div className="card-header mb-5" />
            <div id="form-inputs" className="card-body grid-3">
              <TextField
                required
                label="UNIT NUMBER"
                value={transferParams.unitNumber ?? ''}
                onChange={handleRenumber}
                validate={validateUnitNumber}
              />
              <AsyncSearch<Customer>
                required
                label="TRANSFER ACCOUNT"
                formatOption={({ name }) => name}
                formatValue={customer => customer?.name ?? ''}
                handleSelect={handleSelect}
                search={searchCustomers}
                value={transferParams?.customer}
              />
              <div className="text-field d-flex">
                <input
                  disabled={!transferParams.transferDate}
                  type="datetime-local"
                  className="w-100"
                  id="transfer-date-input"
                  name="transfer-date"
                  max={utcToZonedDateTimeLocal(newZonedDayJs())}
                  min={utcToZonedDateTimeLocal(newZonedDayJs(receivedDate).add(1, 'minute'))}
                  onChange={e => setTransferParams(ps => ({ ...ps, transferDate: dateTimeLocalToUtc(e.target.value) }))}
                  value={utcToZonedDateTimeLocal(transferParams.transferDate)}
                  required
                />
                <label htmlFor="transfer-date-input">TRANSFER DATE</label>
                <button
                  type="button"
                  className="btn-back p-2"
                  onClick={() => setTransferParams(ps => ({ ...ps, transferDate: newZonedDayJs().toISOString() }))}
                >
                  <span className="material-icons icon-large">
                    {!transferParams.transferDate ? 'lock' : 'lock_open'}
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div className="card-primary">
            <div className="card-header mb-5">
              <h4>Generated Transactions</h4>
            </div>
            <div
              id="generated-transactions"
              className="card-body grid-6 align-items-center justify-items-center text-base"
            >
              <span className="gate-out">GATE OUT</span>
              <span>{newZonedDayJs(transferParams.transferDate).subtract(1, 'minute').format('l LT')}</span>
              <span>{unit?.unitNumber}</span>
              <span>{unit?.customer?.name}</span>
              <div className="d-flex g-2">
                <label htmlFor="charge-gate-out-checkbox">CHARGE GATE OUT</label>
                <input
                  id="charge-gate-out-checkbox"
                  type="checkbox"
                  checked={transferParams.chargeGateOut}
                  onChange={e => setTransferParams(prevState => ({ ...prevState, chargeGateOut: e.target.checked }))}
                />
              </div>
              <div className="d-flex g-2">
                <label htmlFor="edi-gate-out-checkbox">EDI GATE OUT</label>
                <input
                  id="edi-gate-out-checkbox"
                  type="checkbox"
                  checked={transferParams.ediGateOut}
                  onChange={e => setTransferParams(prevState => ({ ...prevState, ediGateOut: e.target.checked }))}
                />
              </div>
              <span className="gate-in">GATE IN</span>
              <span>{newZonedDayJs(transferParams.transferDate).format('l LT')}</span>
              <span className={transferParams.unitNumber === unit?.unitNumber ? '' : 'font-weight-bold'}>
                {transferParams.unitNumber ?? unit?.unitNumber}
              </span>
              <span className={transferParams.customer?.id === unit?.customer?.id ? '' : 'font-weight-bold'}>
                {transferParams.customer?.name ?? unit?.customer?.name}
              </span>
              <div className="d-flex g-2">
                <label htmlFor="charge-gate-in-checkbox">CHARGE GATE IN</label>
                <input
                  id="charge-gate-in-checkbox"
                  type="checkbox"
                  checked={transferParams.chargeGateIn}
                  onChange={e => setTransferParams(prevState => ({ ...prevState, chargeGateIn: e.target.checked }))}
                />
              </div>
              <div className="d-flex g-2">
                <label htmlFor="edi-gate-in-checkbox">EDI GATE IN</label>
                <input
                  id="edi-gate-in-checkbox"
                  type="checkbox"
                  checked={transferParams.ediGateIn}
                  onChange={e => setTransferParams(prevState => ({ ...prevState, ediGateIn: e.target.checked }))}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </Loader>
  );
};

export default RenumberUnit;
